import { useMemo } from "react";
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, ScatterChart, Scatter,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { TrendingUp, TrendingDown } from "lucide-react";
import { ResultTable } from "@/components/chat/ResultTable";

const ACCENT = "#14b8a6";
const PALETTE = ["#14b8a6", "#6366f1", "#f59e0b", "#ef4444", "#22c55e", "#06b6d4", "#a855f7", "#ec4899"];
const AXIS = "rgba(148,163,184,0.6)";
const GRID = "rgba(148,163,184,0.15)";

function fmt(v) {
  if (typeof v === "number") {
    if (Math.abs(v) >= 1000) return v.toLocaleString(undefined, { maximumFractionDigits: 0 });
    return Number.isInteger(v) ? v.toString() : v.toFixed(2);
  }
  return String(v ?? "");
}

function shortX(v) {
  const s = String(v ?? "");
  return s.length > 10 ? s.slice(0, 10) : s;
}

function StatCard({ chart }) {
  const d = chart.delta;
  const up = d && d.delta_pct >= 0;
  return (
    <div data-testid="chat-chart-stat" className="rounded-xl border border-border/60 bg-card/40 p-6">
      <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">{chart.label?.replace(/_/g, " ")}</div>
      <div className="mt-2 text-4xl font-bold tabular-nums">{fmt(chart.value)}</div>
      {d && d.delta_pct != null && (
        <div className={`mt-3 inline-flex items-center gap-1.5 text-sm ${up ? "text-emerald-400" : "text-red-400"}`}>
          {up ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
          {Math.abs(d.delta_pct)}% vs prior {d.grain} ({fmt(d.prior)})
        </div>
      )}
    </div>
  );
}

function GeoHeat({ chart }) {
  const { region, value, data } = chart;
  const vals = data.map((r) => r[value] ?? 0);
  const min = Math.min(...vals), max = Math.max(...vals);
  const intensity = (v) => (max === min ? 0.6 : 0.2 + 0.8 * ((v - min) / (max - min)));
  const sorted = [...data].sort((a, b) => (b[value] ?? 0) - (a[value] ?? 0));
  return (
    <div data-testid="chat-chart-choropleth" className="rounded-xl border border-border/60 bg-card/40 p-4">
      <div className="mb-3 text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
        {value.replace(/_/g, " ")} by {region}
      </div>
      <div className="flex flex-wrap gap-2">
        {sorted.map((r, i) => (
          <div
            key={i}
            className="flex h-16 w-16 flex-col items-center justify-center rounded-md border border-border/40"
            style={{ backgroundColor: `rgba(20,184,166,${intensity(r[value] ?? 0)})` }}
            title={`${r[region]}: ${fmt(r[value])}`}
          >
            <span className="text-sm font-bold text-white">{r[region]}</span>
            <span className="text-[10px] text-white/90">{fmt(r[value])}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function GroupedLine({ chart }) {
  const { x, series, y, data } = chart;
  const { rows, keys } = useMemo(() => {
    const ks = [...new Set(data.map((r) => r[series]))];
    const xs = [...new Set(data.map((r) => r[x]))];
    const out = xs.map((xv) => {
      const o = { [x]: xv };
      data.filter((r) => r[x] === xv).forEach((r) => { o[r[series]] = r[y]; });
      return o;
    });
    return { rows: out, keys: ks };
  }, [data, x, series, y]);

  return (
    <ResponsiveContainer width="100%" height={300}>
      <LineChart data={rows} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
        <CartesianGrid stroke={GRID} vertical={false} />
        <XAxis dataKey={x} tick={{ fill: AXIS, fontSize: 11 }} tickFormatter={shortX} />
        <YAxis tick={{ fill: AXIS, fontSize: 11 }} width={48} />
        <Tooltip contentStyle={{ background: "#0b1220", border: "1px solid rgba(148,163,184,0.3)", fontSize: 12 }} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        {keys.map((k, i) => (
          <Line key={k} type="monotone" dataKey={k} stroke={PALETTE[i % PALETTE.length]} dot={false} strokeWidth={2} />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

export function ChartRenderer({ chart }) {
  if (!chart) return null;
  const { type } = chart;

  if (type === "stat") return <StatCard chart={chart} />;
  if (type === "choropleth") return <GeoHeat chart={chart} />;
  if (type === "grouped_line") return <GroupedLine chart={chart} />;

  if (type === "line") {
    return (
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={chart.data} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
          <CartesianGrid stroke={GRID} vertical={false} />
          <XAxis dataKey={chart.x} tick={{ fill: AXIS, fontSize: 11 }} tickFormatter={shortX} minTickGap={24} />
          <YAxis tick={{ fill: AXIS, fontSize: 11 }} width={48} />
          <Tooltip contentStyle={{ background: "#0b1220", border: "1px solid rgba(148,163,184,0.3)", fontSize: 12 }} />
          <Line type="monotone" dataKey={chart.y} stroke={ACCENT} dot={false} strokeWidth={2} />
        </LineChart>
      </ResponsiveContainer>
    );
  }

  if (type === "bar") {
    return (
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={chart.data} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
          <CartesianGrid stroke={GRID} vertical={false} />
          <XAxis dataKey={chart.x} tick={{ fill: AXIS, fontSize: 11 }} tickFormatter={shortX} interval={0} angle={-20} textAnchor="end" height={56} />
          <YAxis tick={{ fill: AXIS, fontSize: 11 }} width={48} />
          <Tooltip contentStyle={{ background: "#0b1220", border: "1px solid rgba(148,163,184,0.3)", fontSize: 12 }} cursor={{ fill: "rgba(148,163,184,0.08)" }} />
          <Bar dataKey={chart.y} fill={ACCENT} radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    );
  }

  if (type === "scatter") {
    return (
      <ResponsiveContainer width="100%" height={300}>
        <ScatterChart margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
          <CartesianGrid stroke={GRID} />
          <XAxis dataKey={chart.x} type="number" name={chart.x} tick={{ fill: AXIS, fontSize: 11 }} />
          <YAxis dataKey={chart.y} type="number" name={chart.y} tick={{ fill: AXIS, fontSize: 11 }} width={48} />
          <Tooltip contentStyle={{ background: "#0b1220", border: "1px solid rgba(148,163,184,0.3)", fontSize: 12 }} />
          <Scatter data={chart.data} fill={ACCENT} />
        </ScatterChart>
      </ResponsiveContainer>
    );
  }

  return <ResultTable columns={chart.columns} rows={chart.data} />;
}
